import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const StrategyTabPane = withListOverflow(StrategyTabListOwner);
// src/features/plan/StrategyTabPane.jsx
//
// MARKETPLAN-STRATEGY-TAB: Dedicated Strategy tab content pane.
// Renders two always-expanded sections:
//   1. Directives table (primary) — full table with header counts, act-now
//      markers, footer note, and "+ Add directive" button.
//   2. Policy panel (recessed) — company baseline assumption cards with the
//      "Manage in Admin →" link, styled quieter than the primary section.
//
// Both sections reuse logic/helpers already exported from UnifiedContextStrip,
// StrategyBand, and PolicyBand. No new API calls — caller passes props.
//
// Props:
//   directives      {Array}    — list of directive objects from getDirectives()
//   canEdit         {boolean}  — show write controls (+ Add directive)
//   onEditDirective {function} — called with (directive|null); null = new
//   baseline        {object|null} — baseline from getPolicyBaseline()
//   overrideCount   {number}   — count of offices with per-office overrides
//   isAdmin         {boolean}  — show "Manage in Admin →" link
//   density         {string}   — "comfortable"|"compact" for directive rows
//   markets         {Array}    — market list for name resolution

import React, { useState } from "react";
import { T } from "../../app/tokens.js";
import { foldText, matchesFoldedQuery } from "../../utils/textSearch.js";
import {
  isActNow,
  DIRECTIVE_TYPE_LABELS,
  DIRECTIVE_TIMEFRAME_LABELS,
  DIRECTIVE_STATUS_LABELS,
} from "./StrategyBand.jsx";
import {
  fmtRtoDays,
  fmtDeskRatio,
  fmtHotelingPct,
  fmtSfFloor,
  fmtHcBuffer,
  fmtConfGiveback,
  fmtTeamBlockWaste,
  fmtEffectiveDate,
} from "./PolicyBand.jsx";

// ─── Shared helpers ───────────────────────────────────────────────────────────

const TIMEFRAME_ORDER = ["now", "lt_1yr", "1_2yr", "2_3yr", "3_plus_yr"];
const ACTIVE_STATUSES = new Set(["aspiration", "in_review", "approved", "in_motion"]);

function sortDirectives(list) {
  return [...list].sort((a, b) => {
    const tA = TIMEFRAME_ORDER.indexOf(a.timeframe);
    const tB = TIMEFRAME_ORDER.indexOf(b.timeframe);
    const tOrd = (tA === -1 ? 999 : tA) - (tB === -1 ? 999 : tB);
    if (tOrd !== 0) return tOrd;
    return (a.title ?? "").localeCompare(b.title ?? "");
  });
}

const TYPE_STYLE = {
  hub_designation:      { color: T.teal,         bg: T.tealSoftBg },
  exit:                 { color: T.ragRed,        bg: T.ragRedBg   },
  new_market:           { color: T.blue,          bg: T.blueSoft   },
  relocate_upgrade:     { color: T.purple,        bg: T.purpleBg   },
  capability_migration: { color: T.amberDark,     bg: T.utilAmber  },
};

const STATUS_STYLE = {
  aspiration: { color: T.mid,           bg: T.lightGray },
  in_review:  { color: T.mid,           bg: T.lightGray },
  approved:   { color: T.utilGreenText, bg: T.utilGreen },
  in_motion:  { color: T.blue,          bg: T.blueSoft  },
  complete:   { color: T.utilGreenText, bg: T.utilGreen },
  cancelled:  { color: T.light,         bg: T.lightGray },
};

function TypeChip({ type }) {
  const style = TYPE_STYLE[type] ?? { color: T.mid, bg: T.lightGray };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", borderRadius: 6, padding: "2px 10px", fontSize: T.fsXs, fontWeight: T.fwSemibold, whiteSpace: "nowrap", color: style.color, background: style.bg }}>
      {DIRECTIVE_TYPE_LABELS[type] ?? type}
    </span>
  );
}

function DirectiveStatusChip({ status }) {
  const style = STATUS_STYLE[status] ?? { color: T.mid, bg: T.lightGray };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", borderRadius: 6, padding: "2px 10px", fontSize: T.fsXs, fontWeight: T.fwSemibold, whiteSpace: "nowrap", color: style.color, background: style.bg }}>
      {DIRECTIVE_STATUS_LABELS[status] ?? status}
    </span>
  );
}

function marketsCellText(directive, nameMap = new Map()) {
  if (!directive.markets || directive.markets.length === 0) return "—";
  return directive.markets
    .map((m) => {
      const name = nameMap.get(m.marketId.toLowerCase()) ?? m.marketId.toUpperCase();
      return m.prospectLabel ? `${name} → ${m.prospectLabel}` : name;
    })
    .join(", ");
}

// ─── Directives section (primary) ─────────────────────────────────────────────

function DirectivesSection({ directives, onEditDirective, density, nameMap }) {
  const [showInactive, setShowInactive] = useState(false);

  const active   = sortDirectives(directives.filter((d) => ACTIVE_STATUSES.has(d.status)));
  const inactive = directives.filter((d) => !ACTIVE_STATUSES.has(d.status));
  const completeCount  = inactive.filter((d) => d.status === "complete").length;
  const cancelledCount = inactive.filter((d) => d.status === "cancelled").length;
  const actNowCount    = directives.filter(isActNow).length;
  const visibleRows    = showInactive ? [...active, ...inactive] : active;

  const thStyle = {
    fontSize:      T.fsXxs,
    letterSpacing: ".07em",
    textTransform: "uppercase",
    color:         "var(--v2-mid)",
    fontWeight:    T.fwBold,
    padding:       "10px 14px",
    textAlign:     "left",
    background:    T.lightGray,
    borderBottom:  "1px solid var(--v2-line)",
    whiteSpace:    "nowrap",
  };
  const tdStyle = {
    fontSize:      T.fsSm,
    color:         T.dark,
    padding:       density === "compact" ? "5px 12px" : "9px 12px",
    borderBottom:  `1px solid ${T.lightGray}`,
    verticalAlign: "middle",
  };

  return (
    <div
      data-testid="strategy-tab-directives"
      style={{
        background:   T.white,
        border:       `1px solid ${T.border}`,
        borderRadius: 12,
        overflow:     "hidden",
        marginBottom: 16,
      }}
    >
      {/* Header */}
      <div style={{
        display:      "flex",
        alignItems:   "center",
        gap:          10,
        padding:      "12px 16px",
        borderBottom: `1px solid ${T.border}`,
        background:   T.white,
      }}>
        <span style={{
          fontSize:      T.fsXxs,
          fontWeight:    T.fwBold,
          letterSpacing: "0.07em",
          textTransform: "uppercase",
          color:         "var(--v2-accent)",
          flexShrink:    0,
        }}>
          ◆ Strategy directives
        </span>
        <span style={{ fontSize: T.fsSm, color: T.mid, fontWeight: T.fwSemibold }}>
          · {directives.length} {directives.length === 1 ? "directive" : "directives"}
        </span>
        {actNowCount > 0 && (
          <span
            data-testid="strategy-tab-act-now-count"
            style={{
              fontSize:     T.fsXs,
              fontWeight:   T.fwSemibold,
              borderRadius: 6,
              padding:      "2px 10px",
              background:   T.ragRedBg,
              color:        T.ragRed,
            }}
          >
            {actNowCount} act now
          </span>
        )}
      </div>

      {/* Table */}
      <div
        data-testid="strategy-band-table"
        style={{ overflowX: "auto" }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsXs }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: "28%", textAlign: "left" }}>Directive</th>
              <th style={{ ...thStyle, textAlign: "left" }}>Type</th>
              <th style={{ ...thStyle, textAlign: "left" }}>Markets</th>
              <th style={{ ...thStyle, textAlign: "left" }}>Timeframe</th>
              <th style={{ ...thStyle, textAlign: "left" }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((d) => (
              <tr
                key={d.id}
                data-testid={`directive-row-${d.id}`}
                style={{ cursor: "pointer" }}
                onClick={() => onEditDirective(d)}
                onMouseEnter={(e) => { e.currentTarget.style.background = T.hoverBg; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}
              >
                <td style={{ ...tdStyle, fontWeight: T.fwSemibold }}>
                  <span>{d.title}</span>
                  {isActNow(d) && (
                    <span
                      data-testid={`act-now-marker-${d.id}`}
                      style={{
                        marginLeft: 6,
                        fontSize:   T.fsXxs,
                        fontWeight: T.fwSemibold,
                        color:      T.ragRed,
                        whiteSpace: "nowrap",
                      }}
                    >
                      ● Act now
                    </span>
                  )}
                </td>
                <td style={tdStyle}>
                  <TypeChip type={d.type} />
                </td>
                <td style={{ ...tdStyle, color: T.mid }}>
                  {marketsCellText(d, nameMap)}
                </td>
                <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                  {DIRECTIVE_TIMEFRAME_LABELS[d.timeframe] ?? d.timeframe}
                </td>
                <td style={tdStyle}>
                  <DirectiveStatusChip status={d.status} />
                </td>
              </tr>
            ))}
            {active.length === 0 && inactive.length === 0 && (
              <tr>
                <td colSpan={5} style={{ ...tdStyle, textAlign: "center", color: T.light }}>
                  No directives yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div style={{
        padding:        "9px 16px",
        fontSize:       T.fsXs,
        color:          T.mid,
        display:        "flex",
        justifyContent: "space-between",
        alignItems:     "center",
        borderTop:      (active.length + inactive.length) > 0 ? `1px solid ${T.border}` : "none",
      }}>
        <span>
          {inactive.length > 0 && (
            <button
              type="button"
              data-testid="strategy-band-inactive-toggle"
              onClick={(e) => { e.stopPropagation(); setShowInactive((v) => !v); }}
              style={{
                background:     "none",
                border:         "none",
                padding:        0,
                cursor:         "pointer",
                color:          T.mid,
                fontSize:       T.fsXs,
                fontWeight:     T.fwSemibold,
                fontFamily:     "inherit",
                textDecoration: "underline",
                whiteSpace:     "nowrap",
              }}
            >
              {`Completed (${completeCount}) · Cancelled (${cancelledCount}) — ${showInactive ? "hide" : "show"}`}
            </button>
          )}
        </span>
      </div>
    </div>
  );
}

// ─── Policy knob cells ────────────────────────────────────────────────────────

const PARAM_ROWS = [
  { label: "Days in office / mo",     key: "rtoDays",           fmt: fmtRtoDays        },
  { label: "Desk sharing",            key: "deskRatio",         fmt: fmtDeskRatio      },
  { label: "Hoteling / visitor hold", key: "hotelingPct",       fmt: fmtHotelingPct    },
  { label: "Conf-room give-back",     key: "confGivebackPct",   fmt: fmtConfGiveback   },
  { label: "Team-block waste",        key: "teamBlockWastePct", fmt: fmtTeamBlockWaste },
  { label: "Density floor",           key: "sfFloor",           fmt: fmtSfFloor        },
  { label: "Target buffer",           key: "hcBufferPct",       fmt: fmtHcBuffer       },
];

function KnobCell({ label, value, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        background:    T.white,
        border:        `1px solid ${T.policyBandCellBorder}`,
        borderRadius:  9,
        padding:       "10px 14px",
        display:       "flex",
        flexDirection: "column",
        gap:           4,
      }}
    >
      <span style={{
        fontSize:      T.fsXxs,
        color:         T.light,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        fontWeight:    T.fwBold,
      }}>
        {label}
      </span>
      <span style={{
        fontSize:   T.fsLg,
        color:      T.dark,
        fontWeight: T.fwSemibold,
        lineHeight: 1.2,
      }}>
        {value}
      </span>
    </div>
  );
}

// ─── Policy section (recessed) ────────────────────────────────────────────────

function PolicySection({ baseline, overrideCount, isAdmin }) {
  if (!baseline) return null;

  const effectiveDateStr = fmtEffectiveDate(baseline.effectiveDate);

  return (
    <div
      data-testid="strategy-tab-policy"
      style={{
        background:   T.policyBandBg,
        border:       `1px solid ${T.policyBandBorder}`,
        borderRadius: 12,
        overflow:     "hidden",
      }}
    >
      {/* Recessed header */}
      <div style={{
        display:      "flex",
        alignItems:   "center",
        gap:          8,
        padding:      "10px 16px",
        borderBottom: `1px solid ${T.policyBandBorder}`,
      }}>
        <span style={{
          fontSize:      T.fsXxs,
          fontWeight:    T.fwBold,
          letterSpacing: "0.07em",
          textTransform: "uppercase",
          color:         "var(--v2-accent)",
          flexShrink:    0,
        }}>
          ◆ Policy
        </span>
        <span style={{ fontSize: T.fsSm, color: T.mid, fontWeight: T.fwSemibold }}>
          · Company baseline
        </span>
        {effectiveDateStr && (
          <span
            data-testid="policy-band-effective-date"
            style={{ fontSize: T.fsXs, color: T.light, whiteSpace: "nowrap" }}
          >
            · Effective {effectiveDateStr}
          </span>
        )}
      </div>

      {/* 4-column knob grid */}
      <div
        data-testid="policy-band-expanded"
        style={{
          display:             "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap:                 10,
          padding:             "14px 16px 10px",
          borderBottom:        `1px solid ${T.policyBandBorder}`,
        }}
      >
        {PARAM_ROWS.map(({ label, key, fmt }) => (
          <KnobCell
            key={key}
            testId={`policy-param-${key}`}
            label={label}
            value={fmt(baseline[key])}
          />
        ))}
      </div>

      {/* Footer: note + override chip + admin link */}
      <div style={{
        padding:        "10px 16px",
        fontSize:       T.fsXs,
        color:          T.mid,
        display:        "flex",
        justifyContent: "space-between",
        alignItems:     "center",
        gap:            12,
        flexWrap:       "wrap",
      }}>
        <span style={{
          display:    "inline-flex",
          alignItems: "center",
          gap:        8,
          flexWrap:   "wrap",
          flexShrink: 0,
        }}>
          {overrideCount > 0 && (
            <span
              data-testid="policy-param-overrides"
              style={{
                display:      "inline-flex",
                alignItems:   "center",
                borderRadius: 6,
                padding:      "2px 10px",
                fontSize:     T.fsXs,
                fontWeight:   T.fwSemibold,
                background:   T.blueSoft,
                color:        T.blue,
                whiteSpace:   "nowrap",
              }}
            >
              {overrideCount} {overrideCount === 1 ? "office" : "offices"} override
            </span>
          )}
          {isAdmin && (
            <a
              data-testid="policy-admin-link"
              href="/admin?section=policy"
              style={{
                color:          "var(--v2-accent)",
                fontSize:       T.fsSm,
                fontWeight:     T.fwSemibold,
                textDecoration: "none",
                whiteSpace:     "nowrap",
                flexShrink:     0,
              }}
            >
              Manage in Admin →
            </a>
          )}
        </span>
      </div>
    </div>
  );
}

// ─── StrategyTabPane (exported) ───────────────────────────────────────────────

function StrategyTabListOwner({
  directives = [],
  canEdit = false,
  onEditDirective,
  baseline = null,
  overrideCount = 0,
  isAdmin = false,
  density = "comfortable",
  markets = [],
  searchQuery = "",
}) {
  const nameMap = new Map(
    (markets ?? []).map((m) => [foldText(m.id), m.name])
  );

  const visibleDirectives = searchQuery.trim()
    ? directives.filter((d) => {
        const q = searchQuery.trim();
        const marketText = (d.markets ?? [])
          .map((m) => nameMap.get(foldText(m.marketId)) ?? m.marketId);
        return (
          matchesFoldedQuery(q, d.title, d.description, ...marketText)
        );
      })
    : directives;

  return (
    <div data-testid="strategy-tab-pane" style={{ padding: "16px 0" }}>
      <DirectivesSection
        directives={visibleDirectives}
        onEditDirective={onEditDirective}
        density={density}
        nameMap={nameMap}
      />
      <PolicySection
        baseline={baseline}
        overrideCount={overrideCount}
        isAdmin={isAdmin}
      />
    </div>
  );
}
